// Pure SVG path math shared by the chart primitives. chart-geometry.test.ts
// pins the exact output strings.

/** Catmull-Rom -> cubic-bezier interpolation through `points`, for a series that
 *  really is a smooth signal. It can overshoot between points (see `linearPath`).
 *  `baseY` clamps a control point from swinging below the chart's baseline.
 *  Returns an SVG path `d` string starting with `M` and continuing with `C`
 *  segments. */
export function catmullRomPath(
  points: ReadonlyArray<readonly [number, number]>,
  baseY: number,
): string {
  return points
    .map(([px, py], i) => {
      if (i === 0) return `M${px.toFixed(1)},${py.toFixed(1)}`;
      const p1 = points[i - 1] ?? ([px, py] as const);
      const p2 = [px, py] as const;
      const p0 = points[Math.max(i - 2, 0)] ?? p1;
      const p3 = points[Math.min(i + 1, points.length - 1)] ?? p2;
      const c1x = p1[0] + (p2[0] - p0[0]) / 6;
      const c1y = Math.min(p1[1] + (p2[1] - p0[1]) / 6, baseY);
      const c2x = p2[0] - (p3[0] - p1[0]) / 6;
      const c2y = Math.min(p2[1] - (p3[1] - p1[1]) / 6, baseY);
      return `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${px.toFixed(1)},${py.toFixed(1)}`;
    })
    .join(" ");
}

/** Straight segments through `points`: the honest path for measured data, and
 *  the line-chart default (the spline is opt-in). A Catmull-Rom spline passes
 *  through every point but WANDERS between them, so a dense, high-variance
 *  series shows maxima, minima and inflections on dates that have no such
 *  value. */
export function linearPath(points: ReadonlyArray<readonly [number, number]>): string {
  return points
    .map(([px, py], i) => `${i === 0 ? "M" : "L"}${px.toFixed(1)},${py.toFixed(1)}`)
    .join(" ");
}

/** One SVG arc `d` string on a circle of radius `r` centered at `(cx, cy)`,
 *  sweeping clockwise from `fromDeg` to `toDeg` (0deg = 12 o'clock). Used by
 *  both Gauge (one ring) and DualGauge (two rings at different radii). */
export function arcPath(cx: number, cy: number, r: number, fromDeg: number, toDeg: number): string {
  const a0 = ((fromDeg - 90) * Math.PI) / 180;
  const a1 = ((toDeg - 90) * Math.PI) / 180;
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M${(cx + r * Math.cos(a0)).toFixed(1)},${(cy + r * Math.sin(a0)).toFixed(1)} A${r},${r} 0 ${large} 1 ${(cx + r * Math.cos(a1)).toFixed(1)},${(cy + r * Math.sin(a1)).toFixed(1)}`;
}
